import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { imagesApi } from '../../services/api';

const DAY_COLORS = ['#EA4335', '#FBBC04', '#34A853', '#4285F4', '#A142F4', '#FF6D00'];
const BLOCK_ICONS = { morning: '🌅', afternoon: '☀️', evening: '🌙' };
const BLOCK_LABELS = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };

/* ─────────────────────────────────────────────────
   usePlacePhotos — Google Places (getDetails for 3 photos) → Wikimedia fallback
   onFirstPhotoLoaded(activityId, photoUrl) called when first photo ready
───────────────────────────────────────────────── */
const usePlacePhotos = (activity, onFirstPhotoLoaded) => {
  const [photos, setPhotos] = useState([]);
  const [placeInfo, setPlaceInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activity) return;
    let cancelled = false;
    setLoading(true);
    setPhotos([]);
    setPlaceInfo(null);

    const notifyFirst = (list) => {
      if (list[0]?.url && onFirstPhotoLoaded) onFirstPhotoLoaded(activity.id, list[0].url);
    };

    // Fill up to 3 slots with Unsplash images (appended after existing)
    const fillWithUnsplash = async (existing = []) => {
      const need = 3 - existing.length;
      if (need <= 0) { if (!cancelled) setLoading(false); return; }
      try {
        const res = await imagesApi.searchImages(activity.place || activity.name, need, 'unsplash');
        if (cancelled) return;
        const imgs = (res.data || [])
          .filter(img => img.imageUrl && img.mimeType?.startsWith('image/'))
          .slice(0, need)
          .map(img => ({ url: img.imageUrl, attribution: img.attributionText }));
        const combined = [...existing, ...imgs].slice(0, 3);
        setPhotos(combined);
        if (existing.length === 0) notifyFirst(combined);
      } catch { /* ignore */ }
      if (!cancelled) setLoading(false);
    };

    const tryGoogle = () => {
      if (!window.google?.maps?.places?.PlacesService) return false;

      const svc = new window.google.maps.places.PlacesService(document.createElement('div'));

      // Step 1: find place_id (cheap — Basic Data only)
      const findReq = {
        query: activity.place || activity.name,
        fields: ['place_id'],
      };
      if (activity.latitude && activity.longitude) {
        findReq.locationBias = {
          center: { lat: parseFloat(activity.latitude), lng: parseFloat(activity.longitude) },
          radius: 3000,
        };
      }

      svc.findPlaceFromQuery(findReq, (results, status) => {
        if (cancelled) return;
        if (status !== 'OK' || !results?.[0]?.place_id) { fillWithUnsplash(); return; }

        // Step 2: getDetails with place_id — returns up to 10 photos + rich metadata
        svc.getDetails(
          {
            placeId: results[0].place_id,
            fields: [
              'photos', 'rating', 'user_ratings_total', 'price_level',
              'types', 'opening_hours', 'vicinity', 'formatted_address',
              'website', 'formatted_phone_number',
            ],
          },
          (detail, dStatus) => {
            if (cancelled) return;
            if (dStatus !== 'OK' || !detail) { fillWithUnsplash(); return; }

            const photoList = (detail.photos || [])
              .slice(0, 3)
              .map(p => ({
                url: p.getUrl({ maxWidth: 800, maxHeight: 600 }),
              }));

            setPlaceInfo({
              rating: detail.rating ?? null,
              reviewCount: detail.user_ratings_total ?? null,
              priceLevel: detail.price_level ?? null,
              types: detail.types ?? [],
              openNow: detail.opening_hours?.isOpen?.() ?? null,
              weekdayText: detail.opening_hours?.weekday_text ?? null,
              vicinity: detail.vicinity || detail.formatted_address || null,
              website: detail.website || null,
              phone: detail.formatted_phone_number || null,
            });

            if (photoList.length > 0) {
              setPhotos(photoList);
              notifyFirst(photoList);
              // Supplement remaining slots with Unsplash if < 3
              fillWithUnsplash(photoList);
            } else {
              fillWithUnsplash();
            }
          }
        );
      });
      return true;
    };

    if (!tryGoogle()) fillWithUnsplash();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.id]);

  return { photos, placeInfo, loading };
};

/* ─── Today's hours from weekday_text array ─── */
// weekday_text is [Monday, Tuesday, ..., Sunday] — index 0 = Monday, 6 = Sunday
const getTodayHours = (weekdayText) => {
  if (!weekdayText?.length) return null;
  const dayJs = new Date().getDay(); // 0=Sun
  const idx = dayJs === 0 ? 6 : dayJs - 1; // convert to Mon-based
  const text = weekdayText[idx] || '';
  return text.replace(/^[^:]+:\s*/, '').trim() || null; // strip "Monday: "
};

const formatPriceLevel = (level) => {
  if (level == null) return null;
  if (level === 0) return 'Free';
  return '$'.repeat(level);
};

const formatPlaceType = (types) => {
  if (!types?.length) return null;
  const skip = new Set(['point_of_interest', 'establishment', 'premise', 'political']);
  const first = types.find(t => !skip.has(t));
  if (!first) return null;
  return first.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const renderStars = (rating) => {
  if (!rating) return null;
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(5 - full - (half ? 1 : 0));
};

/* ═══ Styled components ═══ */

const Overlay = styled(motion.div)`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 100;
`;

const Sheet = styled(motion.div)`
  background: #111827;
  display: flex;
  flex-direction: column;

  /* Mobile bottom-sheet */
  ${p => !p.$desktop && `
    position: fixed; bottom: 0; left: 0;
    width: 100%; max-height: 88vh;
    border-radius: 28px 28px 0 0;
    z-index: 101;
    overflow: hidden;
  `}

  /* Desktop side-panel — slides in from left inside MapFrame */
  ${p => p.$desktop && `
    position: absolute; top: 0; left: 0; bottom: 0;
    width: min(380px, 100%);
    border-radius: 0 24px 24px 0;
    z-index: 10;
    box-shadow: 8px 0 32px rgba(0,0,0,0.35);
    overflow: hidden;
  `}
`;

/* ─── Photo grid ─── */
const PhotoArea = styled.div`
  display: flex;
  height: 210px;
  flex-shrink: 0;
  gap: 3px;
  background: #1f2937;
  overflow: hidden;
`;

const MainSlot = styled.div`
  flex: 2;
  background: ${p => p.$bg || '#374151'};
  overflow: hidden;
`;

const SideSlots = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const SideSlot = styled.div`
  flex: 1;
  background: ${p => p.$bg || '#374151'};
  overflow: hidden;
`;

const Photo = styled.img`
  width: 100%; height: 100%;
  object-fit: cover; display: block;
`;

const PhotoPlaceholder = styled.div`
  width: 100%; height: 100%;
  background: ${p => p.$bg};
  display: flex; align-items: center; justify-content: center;
  font-size: ${p => p.$large ? '60px' : '28px'};
  opacity: ${p => p.$large ? 1 : 0.6};
`;

const Shimmer = styled.div`
  width: 100%; height: 100%;
  background: linear-gradient(90deg, #1f2937 25%, #374151 50%, #1f2937 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

/* ─── Body ─── */
const Body = styled.div`
  overflow-y: auto; flex: 1;
  min-height: 0;
  padding: 18px 20px calc(max(20px, env(safe-area-inset-bottom)) + 56px) 20px;
  &::-webkit-scrollbar { display: none; }
`;

const TitleRow = styled.div`
  display: flex; align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const PlaceTitle = styled.h2`
  font-size: 22px; font-weight: 700;
  color: #f9fafb; margin: 0;
  flex: 1; padding-right: 12px;
  line-height: 1.25;
`;

const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  border: none; color: #d1d5db; cursor: pointer;
  flex-shrink: 0; font-size: 16px; line-height: 1;
  &:active { transform: scale(0.9); background: rgba(255,255,255,0.18); }
`;

const RatingRow = styled.div`
  display: flex; align-items: center;
  gap: 6px; margin-bottom: 4px;
`;

const Stars = styled.span`
  color: #fbbf24; font-size: 14px; letter-spacing: 1px;
`;

const RatingNum = styled.span`
  font-size: 14px; font-weight: 700; color: #f9fafb;
`;

const PlaceTypeText = styled.span`
  font-size: 13px; color: #9ca3af;
`;

const OpenBadge = styled.span`
  font-size: 12px; font-weight: 700;
  color: ${p => p.$open ? '#34d399' : '#f87171'};
`;

const MetaRow = styled.div`
  display: flex; align-items: center;
  gap: 8px; margin-bottom: 18px; flex-wrap: wrap;
`;

const DayChip = styled.span`
  padding: 3px 10px;
  background: ${p => p.$color + '2a'};
  color: ${p => p.$color};
  border-radius: 10px;
  font-size: 12px; font-weight: 800; letter-spacing: 0.04em;
`;

const TimeLabel = styled.span`font-size: 13px; color: #9ca3af;`;
const DurationLabel = styled.span`font-size: 13px; color: #9ca3af;`;
const Dot = styled.span`font-size: 10px; color: #4b5563;`;
const ReviewCount = styled.span`font-size: 13px; color: #6b7280;`;
const PriceChip = styled.span`
  font-size: 13px; font-weight: 700;
  color: #34d399;
`;

const AddressRow = styled.div`
  display: flex; align-items: flex-start;
  gap: 7px; margin-bottom: 18px;
  svg { width: 14px; height: 14px; flex-shrink: 0; color: #6b7280; margin-top: 1px; }
`;
const AddressText = styled.span`
  font-size: 13px; color: #9ca3af; line-height: 1.4;
`;

/* ─── Opening hours ─── */
const HoursCard = styled.div`
  background: rgba(255,255,255,0.06);
  border-radius: 16px; padding: 14px 16px;
  margin-bottom: 18px;
`;

const HoursMainRow = styled.div`
  display: flex; align-items: center;
  justify-content: space-between;
`;

const HoursStatus = styled.span`
  font-size: 13px; font-weight: 700;
  color: ${p => p.$open ? '#34d399' : '#f87171'};
`;

const HoursTodayText = styled.span`
  font-size: 13px; color: #9ca3af; margin-left: 6px;
`;

const HoursToggleBtn = styled.button`
  background: none; border: none; padding: 0;
  color: #60a5fa; font-size: 12px; cursor: pointer;
  display: flex; align-items: center; gap: 4px;
  flex-shrink: 0;
  svg { width: 14px; height: 14px;
    transition: transform 0.2s;
    transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const WeekList = styled.div`
  margin-top: 12px; display: flex; flex-direction: column; gap: 5px;
`;

const WeekRow = styled.div`
  display: flex; justify-content: space-between;
  font-size: 12px;
  color: ${p => p.$today ? '#f9fafb' : '#6b7280'};
  font-weight: ${p => p.$today ? '600' : '400'};
`;

/* ─── Actions ─── */
const ActionRow = styled.div`
  display: flex; gap: 10px; margin-bottom: 22px;
`;

const ActionBtn = styled.a`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; gap: 7px;
  padding: 11px 6px;
  background: rgba(255,255,255,0.07);
  border-radius: 14px; color: #93c5fd;
  font-size: 12px; font-weight: 600;
  text-decoration: none; border: none; cursor: pointer;
  svg { width: 20px; height: 20px; }
  &:active { transform: scale(0.95); background: rgba(255,255,255,0.12); }
`;

/* ─── Description / Highlights ─── */
const HighlightsCard = styled.div`
  background: rgba(255,255,255,0.06);
  border-radius: 16px; padding: 15px 16px; margin-bottom: 18px;
`;
const HighlightsHeader = styled.div`
  font-size: 13px; font-weight: 700; color: #e5e7eb; margin-bottom: 10px;
`;
const Bullet = styled.div`
  font-size: 13px; color: #9ca3af;
  padding: 3px 0 3px 14px; position: relative;
  &::before { content: '•'; position: absolute; left: 0; color: #6b7280; }
`;
const DescText = styled.p`
  font-size: 13px; color: #9ca3af; line-height: 1.65; margin: 0 0 20px 0;
`;
const OpenMapsBtn = styled.a`
  display: flex; align-items: center; justify-content: center;
  gap: 8px; width: 100%; padding: 15px;
  background: #3b5bdb; border-radius: 16px;
  color: white; font-size: 15px; font-weight: 700;
  text-decoration: none; box-sizing: border-box;
  &:active { transform: scale(0.98); background: #2f4ac2; }
`;

/* ─── Lightbox ─── */
const LightboxOverlay = styled(motion.div)`
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.96);
  z-index: 300;
  display: flex; align-items: center; justify-content: center;
`;

const LightboxImg = styled(motion.img)`
  max-width: 100vw;
  max-height: 100dvh;
  width: 100%;
  object-fit: contain;
  display: block;
  touch-action: pinch-zoom;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: max(16px, env(safe-area-inset-top));
  right: 16px;
  width: 38px; height: 38px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  border: none; color: white;
  font-size: 18px; line-height: 1;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:active { background: rgba(255,255,255,0.28); transform: scale(0.9); }
`;

/* ─── Helpers ─── */
const buildMapsUrl = (a) => {
  if (a.latitude && a.longitude)
    return `https://www.google.com/maps/search/?api=1&query=${a.latitude},${a.longitude}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.place || a.name || '')}`;
};

const buildDirectionsUrl = (a) => {
  if (a.latitude && a.longitude)
    return `https://www.google.com/maps/dir/?api=1&destination=${a.latitude},${a.longitude}&travelmode=walking`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a.place || a.name || '')}`;
};

const extractHighlights = (desc) =>
  (desc || '').split(/[.;]/).map(s => s.trim()).filter(s => s.length > 15 && s.length < 120).slice(0, 3);

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* ═══════════════════════════════════════════════
   Component
═══════════════════════════════════════════════ */
const PlaceDetailSheet = ({ activity, onClose, onFirstPhotoLoaded, variant = 'sheet' }) => {
  const isDesktop = variant === 'desktop';
  const { photos, placeInfo, loading } = usePlacePhotos(activity, onFirstPhotoLoaded);
  const [showAllHours, setShowAllHours] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Reset panels when switching activity
  useEffect(() => { setShowAllHours(false); setLightboxPhoto(null); }, [activity?.id]);

  if (!activity) return null;

  const color = DAY_COLORS[(activity.dayIdx || 0) % DAY_COLORS.length];
  const blockIcon = BLOCK_ICONS[activity.block] || '📍';
  const timeLabel = BLOCK_LABELS[activity.block] || activity.time || '';
  const mapsUrl = buildMapsUrl(activity);
  const directionsUrl = buildDirectionsUrl(activity);
  const highlights = extractHighlights(activity.description);
  const placeType = formatPlaceType(placeInfo?.types);
  const priceLabel = formatPriceLevel(placeInfo?.priceLevel);

  const todayHours = getTodayHours(placeInfo?.weekdayText);
  const todayDayIdx = new Date().getDay(); // 0=Sunday

  const handleShare = (e) => {
    e.preventDefault();
    if (navigator.share) navigator.share({ title: activity.name, url: mapsUrl }).catch(() => {});
    else navigator.clipboard.writeText(mapsUrl).catch(() => {});
  };

  const renderSlot = (idx, large) => {
    if (loading) return <Shimmer />;
    const p = photos[idx];
    if (p?.url) return (
      <Photo
        src={p.url}
        alt={activity.name}
        loading="lazy"
        style={{ cursor: 'zoom-in' }}
        onClick={(e) => { e.stopPropagation(); setLightboxPhoto(p.url); }}
      />
    );
    return (
      <PhotoPlaceholder $bg={color + (large ? '44' : '28')} $large={large}>
        {large ? blockIcon : '📸'}
      </PhotoPlaceholder>
    );
  };

  return (
    <>
    <AnimatePresence>
      {!isDesktop && (
        <Overlay key="ov" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      )}
      <Sheet
        key="sh"
        $desktop={isDesktop}
        initial={isDesktop ? { x: '-100%' } : { y: '100%' }}
        animate={isDesktop ? { x: 0 } : { y: 0 }}
        exit={isDesktop ? { x: '-100%' } : { y: '100%' }}
        transition={{ type: 'spring', damping: 32, stiffness: 320 }}
      >
        {/* ─── Photo grid (3 slots) ─── */}
        <PhotoArea>
          <MainSlot $bg={color + '44'}>{renderSlot(0, true)}</MainSlot>
          <SideSlots>
            <SideSlot $bg={color + '28'}>{renderSlot(1, false)}</SideSlot>
            <SideSlot $bg={color + '22'}>{renderSlot(2, false)}</SideSlot>
          </SideSlots>
        </PhotoArea>

        <Body>
          {/* ─── Title + close ─── */}
          <TitleRow>
            <PlaceTitle>{activity.name}</PlaceTitle>
            <CloseBtn onClick={onClose} aria-label="Close">✕</CloseBtn>
          </TitleRow>

          {/* ─── Rating · review count · type · price · open now ─── */}
          {(placeInfo?.rating || placeType || placeInfo?.openNow != null) && (
            <RatingRow>
              {placeInfo?.rating && (
                <>
                  <RatingNum>{placeInfo.rating.toFixed(1)}</RatingNum>
                  <Stars>{renderStars(placeInfo.rating)}</Stars>
                  {placeInfo.reviewCount && (
                    <ReviewCount>({placeInfo.reviewCount.toLocaleString()})</ReviewCount>
                  )}
                </>
              )}
              {placeType && (
                <><Dot>·</Dot><PlaceTypeText>{placeType}</PlaceTypeText></>
              )}
              {priceLabel && (
                <><Dot>·</Dot><PriceChip>{priceLabel}</PriceChip></>
              )}
              {placeInfo?.openNow != null && (
                <><Dot>·</Dot><OpenBadge $open={placeInfo.openNow}>{placeInfo.openNow ? 'Open' : 'Closed'}</OpenBadge></>
              )}
            </RatingRow>
          )}

          {/* ─── Day · time · duration ─── */}
          <MetaRow>
            <DayChip $color={color}>DAY {activity.dayNum}</DayChip>
            {timeLabel && <TimeLabel>{timeLabel}</TimeLabel>}
            {activity.duration && <><Dot>·</Dot><DurationLabel>⏱ {activity.duration}</DurationLabel></>}
          </MetaRow>

          {/* ─── Address ─── */}
          {placeInfo?.vicinity && (
            <AddressRow>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              <AddressText>{placeInfo.vicinity}</AddressText>
            </AddressRow>
          )}

          {/* ─── Action buttons ─── */}
          <ActionRow>
            <ActionBtn href={directionsUrl} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="3 11 22 2 13 21 11 13 3 11" />
              </svg>
              Directions
            </ActionBtn>
            {placeInfo?.website && (
              <ActionBtn href={placeInfo.website} target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                Website
              </ActionBtn>
            )}
            {placeInfo?.phone && (
              <ActionBtn href={`tel:${placeInfo.phone}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6 6l.93-.93a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.5 16.16z"/>
                </svg>
                Call
              </ActionBtn>
            )}
            <ActionBtn as="button" onClick={handleShare}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              Share
            </ActionBtn>
          </ActionRow>

          {/* ─── Opening hours ─── */}
          {(placeInfo?.openNow != null || todayHours || placeInfo?.weekdayText) && (
            <HoursCard>
              <HoursMainRow>
                <div>
                  <HoursStatus $open={placeInfo?.openNow}>
                    {placeInfo?.openNow ? 'Open now' : placeInfo?.openNow === false ? 'Closed now' : ''}
                  </HoursStatus>
                  {todayHours && (
                    <HoursTodayText>
                      {placeInfo?.openNow != null && ' · '}{todayHours}
                    </HoursTodayText>
                  )}
                </div>
                {placeInfo?.weekdayText?.length > 0 && (
                  <HoursToggleBtn $open={showAllHours} onClick={() => setShowAllHours(p => !p)}>
                    Hours
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </HoursToggleBtn>
                )}
              </HoursMainRow>

              <AnimatePresence>
                {showAllHours && placeInfo?.weekdayText && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <WeekList>
                      {placeInfo.weekdayText.map((line, i) => {
                        // line format: "Monday: 9:00 AM – 6:00 PM"
                        const colonIdx = line.indexOf(':');
                        const dayName = colonIdx > 0 ? line.slice(0, colonIdx) : line;
                        const hours = colonIdx > 0 ? line.slice(colonIdx + 1).trim() : '';
                        // Convert Mon-based index (0=Mon) back to JS day (0=Sun)
                        const jsDay = i === 6 ? 0 : i + 1;
                        const isToday = jsDay === todayDayIdx;
                        return (
                          <WeekRow key={i} $today={isToday}>
                            <span>{isToday ? `${DAY_NAMES_SHORT[jsDay]} (today)` : dayName}</span>
                            <span>{hours}</span>
                          </WeekRow>
                        );
                      })}
                    </WeekList>
                  </motion.div>
                )}
              </AnimatePresence>
            </HoursCard>
          )}

          {/* ─── Description / highlights ─── */}
          {activity.description && (
            highlights.length > 1 ? (
              <HighlightsCard>
                <HighlightsHeader>About this stop</HighlightsHeader>
                {highlights.map((h, i) => <Bullet key={i}>{h}</Bullet>)}
              </HighlightsCard>
            ) : (
              <DescText>{activity.description}</DescText>
            )
          )}

          <OpenMapsBtn href={mapsUrl} target="_blank" rel="noopener noreferrer">
            Open in Google Maps
          </OpenMapsBtn>
        </Body>
      </Sheet>
    </AnimatePresence>

    {/* ─── Lightbox ─── */}
    <AnimatePresence>
      {lightboxPhoto && (
        <LightboxOverlay
          key="lb"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => setLightboxPhoto(null)}
        >
          <LightboxImg
            src={lightboxPhoto}
            alt={activity.name}
            initial={{ scale: 0.88, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.88, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
          />
          <LightboxClose onClick={() => setLightboxPhoto(null)}>✕</LightboxClose>
        </LightboxOverlay>
      )}
    </AnimatePresence>
    </>
  );
};

export default PlaceDetailSheet;
